// Scheduling's pure helpers: no Vue, like driverFormat.js one directory over.
// Everything a board cell, a decision notice or a request row prints comes
// through here, so a wording change happens once.
import moment from 'moment-timezone';

export const DOW = Object.freeze([
  { value: 0, label: 'Sunday', short: 'Sun' },
  { value: 1, label: 'Monday', short: 'Mon' },
  { value: 2, label: 'Tuesday', short: 'Tue' },
  { value: 3, label: 'Wednesday', short: 'Wed' },
  { value: 4, label: 'Thursday', short: 'Thu' },
  { value: 5, label: 'Friday', short: 'Fri' },
  { value: 6, label: 'Saturday', short: 'Sat' },
]);

export const ZONES = Object.freeze([
  'America/Los_Angeles',
  'America/Denver',
  'America/Phoenix',
  'America/Chicago',
  'America/New_York',
  'America/Anchorage',
  'Pacific/Honolulu',
]);

/** What each decision reason means to a dispatcher, in one short phrase. */
export const REASON_LABELS = Object.freeze({
  SCHEDULE_NOT_CONFIGURED: 'Availability not configured',
  OFF_SCHEDULE: 'Off schedule',
  TIME_OFF_APPROVED: 'Approved time off',
  UNAVAILABLE_NOW: 'Reported unavailable',
  TIME_OFF_PENDING: 'Pending time-off request',
  DOUBLE_BOOKED_DRIVER: 'Already on another load',
  DOUBLE_BOOKED_TRUCK: 'Truck already on another load',
  TRUCK_INACTIVE: 'Truck marked inactive',
  EXTERNAL_BUSY: 'External calendar busy',
  EXTERNAL_STALE: 'External calendar stale',
  END_UNKNOWN: 'End time unknown',
  TRAVEL_TIME_SHORT: 'Can’t get there in time',
  STOP_TRAVEL_TIME_SHORT: 'Can’t reach this stop in time',
  NO_FREE_TIME: 'No free time in this range',
  // Spec 37/38: readiness blocks (api/app/Services/Readiness). An expired
  // credential, or a defect whose repair is not certified and that the
  // driver did not flag, can be assigned over with a reason; a hold cannot.
  // OUT_OF_SERVICE is the company's own hold: "out of service" is a
  // regulatory term (a roadside order, 49 CFR 396.9(c)) and never its label.
  CREDENTIAL_EXPIRED: 'Credential expired',
  CREDENTIAL_EXPIRED_ARCHIVED: 'Credential expired and archived',
  CREDENTIAL_MISSING: 'Credential not on file',
  OUT_OF_SERVICE: 'Held: not available',
  DEFECT_UNCERTIFIED: 'Defect repair not certified',
  // Spec 46: a started work order, and a trailer on two loads at once (an
  // advisory: reported, never refused).
  IN_SHOP: 'In the shop',
  DOUBLE_BOOKED_TRAILER: 'Trailer already on another load',
});

/**
 * Does any reason refuse even with a typed reason? The server marks a
 * readiness reason it will never let a reason talk past (OUT_OF_SERVICE, a hold)
 * `overridable: false`; the scheduler's own never-overridable codes arrive
 * in `override.with_reason` instead.
 */
export function hasHardBlock(reasons) {
  return (reasons || []).some(r => r && r.overridable === false);
}

export function reasonLabel(code) {
  return REASON_LABELS[code] || code;
}

/** The LwNotice tone for a decision status. */
export function decisionTone(status) {
  if (status === 'available') return 'success';
  if (status === 'blocked') return 'danger';
  if (status === 'needs_review' || status === 'unknown') return 'warning';
  return 'info';
}

export function decisionTitle(status) {
  return (
    {
      available: 'Available',
      blocked: 'Not available',
      needs_review: 'Needs review',
      unknown: 'Availability unknown',
    }[status] || 'Availability'
  );
}

/** "Mon 10/19 8:00 AM" in the zone, or an em dash. */
export function fmtTime(value, zone) {
  if (!value) return '—';
  const m = moment(value);
  if (!m.isValid()) return '—';
  return m.tz(zone).format('ddd M/D h:mm A');
}

/** "8:00 AM" in the zone. */
export function fmtClock(value, zone) {
  if (!value) return '—';
  const m = moment(value);
  return m.isValid() ? m.tz(zone).format('h:mm A') : '—';
}

/** "Mon 10/19 8:00 AM – 5:00 PM", or "– open end" when the end is unknown. */
export function fmtRange(start, end, zone) {
  if (!start) return '—';
  if (!end) return `${fmtTime(start, zone)} – open end`;
  const s = moment(start).tz(zone);
  const e = moment(end).tz(zone);
  if (s.isSame(e, 'day')) return `${s.format('ddd M/D h:mm A')} – ${e.format('h:mm A')}`;
  return `${s.format('ddd M/D h:mm A')} – ${e.format('ddd M/D h:mm A')}`;
}

/** "4h 50m", "45m", "3h". */
export function minutesLabel(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (!h) return `${m}m`;
  return m ? `${h}h ${m}m` : `${h}h`;
}

/**
 * One travel leg between this load and a neighbouring one, as a line under a
 * driver's name (AvailabilityService.travelFor's legs): the drive, where it
 * runs from or to, and when it arrives against when it is due.
 */
export function legLine(leg, zone) {
  const load = leg.po || `#${leg.load_id}`;
  const drive = `${minutesLabel(leg.drive_minutes)} drive (${leg.distance} ${leg.unit})`;
  const arrives = fmtTime(leg.arrives_at, zone);
  const due = fmtTime(leg.deadline, zone);
  if (leg.direction === 'from_previous') {
    return leg.feasible
      ? `${drive} from load ${load} at ${leg.from_name}; arrives ${arrives}.`
      : `${drive} from load ${load} at ${leg.from_name}: arrives ${arrives}, due ${due}.`;
  }
  return leg.feasible
    ? `${drive} on to load ${load} at ${leg.to_name}; arrives ${arrives}.`
    : `${drive} on to load ${load} at ${leg.to_name}: arrives ${arrives}, due ${due}.`;
}

/**
 * What sits under a driver's name in a picker: the travel leg that does not
 * fit, then the other reasons' sentences, at most two lines; for an
 * available driver, the drive from or to their neighbouring load if any.
 */
export function availabilityLines(row, zone) {
  const legs = (row && row.travel && row.travel.legs) || [];
  if (!row || !row.status || row.status === 'available') {
    const fits = legs.find(l => l.feasible && l.drive_minutes != null);
    return fits ? [legLine(fits, zone)] : [];
  }
  const lines = legs.filter(l => l.feasible === false).map(l => legLine(l, zone));
  for (const reason of row.reasons || []) {
    if (reason.code === 'TRAVEL_TIME_SHORT') continue;
    lines.push(reason.message || reasonLabel(reason.code));
  }
  return lines.slice(0, 2);
}

/** The chip a picker row shows: "Available", or the first reason's label. */
export function availabilityChipLabel(row) {
  if (row.status === 'available') return 'Available';
  const first = (row.reasons || [])[0];
  return first ? reasonLabel(first.code) : decisionTitle(row.status);
}

/** 'YYYY-MM-DD' of today in the zone. */
export function todayIn(zone) {
  return moment().tz(zone).format('YYYY-MM-DD');
}

export function addDays(dateStr, days) {
  return moment(dateStr, 'YYYY-MM-DD').add(days, 'days').format('YYYY-MM-DD');
}

/** The Monday of the week holding `dateStr`. */
export function weekStart(dateStr) {
  const m = moment(dateStr, 'YYYY-MM-DD');
  const dow = m.day();
  return m.subtract(dow === 0 ? 6 : dow - 1, 'days').format('YYYY-MM-DD');
}

/** "Mon 10/19" for a column header. */
export function fmtDate(dateStr) {
  return moment(dateStr, 'YYYY-MM-DD').format('ddd M/D');
}

/** Does [start, end) touch the local day `dateStr` in `zone`? */
export function onDay(start, end, dateStr, zone) {
  if (!start) return false;
  const dayStart = moment.tz(dateStr, 'YYYY-MM-DD', zone);
  const dayEnd = dayStart.clone().add(1, 'day');
  const s = moment(start);
  const e = end ? moment(end) : s.clone().add(1, 'minute');
  return s.isBefore(dayEnd) && e.isAfter(dayStart);
}

/**
 * "Mon–Fri 08:00–17:00; Sat 06:00–12:00": consecutive days with identical
 * hours collapse into a range.
 */
export function windowsSummary(windows) {
  if (!Array.isArray(windows) || windows.length === 0) return 'No hours';
  const byDay = new Map();
  for (const w of windows) {
    const key = `${w.start}–${w.end}`;
    if (!byDay.has(w.dow)) byDay.set(w.dow, []);
    byDay.get(w.dow).push(key);
  }
  const sig = dow => (byDay.get(dow) || []).slice().sort().join(', ');
  const parts = [];
  let i = 0;
  while (i < 7) {
    if (!byDay.has(i)) {
      i += 1;
      continue;
    }
    let j = i;
    while (j + 1 < 7 && byDay.has(j + 1) && sig(j + 1) === sig(i)) j += 1;
    const days = j > i ? `${DOW[i].short}–${DOW[j].short}` : DOW[i].short;
    parts.push(`${days} ${sig(i)}`);
    i = j + 1;
  }
  return parts.join('; ');
}

export function isOvernight(w) {
  return !!(w && w.start && w.end && w.end <= w.start);
}

/**
 * The sentence a failed assignment shows. A scheduling 409 carries the
 * decision; anything else falls back to the problem's title.
 */
export function assignmentProblem(response) {
  const data = response && response.data;
  if (!data) return { title: 'The assignment could not be saved.', reasons: [], decision: null };
  if (data.error_code === 'SCHEDULING_BLOCKED' || data.error_code === 'SCHEDULING_NEEDS_REVIEW') {
    const decision = data.decision || { status: 'blocked', reasons: [] };
    return {
      title: data.title,
      reasons: (decision.reasons || []).map(r => r.message || reasonLabel(r.code)),
      decision,
      blocking: data.blocking || [],
      code: data.error_code,
    };
  }
  return { title: data.title || 'The assignment could not be saved.', reasons: [], decision: null };
}

export function timeOffStatusChip(status) {
  return (
    {
      pending: { value: 'warning', label: 'Pending' },
      approved: { value: 'success', label: 'Approved' },
      declined: { value: 'neutral', label: 'Declined' },
      cancelled: { value: 'neutral', label: 'Cancelled' },
    }[status] || { value: 'neutral', label: status }
  );
}

/** Who may change schedules: scheduling.manage is admin and dispatcher (D-01). */
export function canManageScheduling(store) {
  const getters = (store && store.getters) || {};
  if (getters.isAdmin) return true;
  const user = getters.user;
  return !!(user && user.role === 'dispatcher');
}

// ------------------------------------------------- equipment (spec 46)

/**
 * The LwChip feedback value for a unit's derived state (EquipmentAvailability
 * headline): free is success, on a load is plain information, a warning
 * needs a look, blocked and in the shop are danger, inactive is neutral.
 */
export function unitStateTone(state) {
  return (
    {
      available: 'success',
      on_load: 'info',
      needs_review: 'warning',
      blocked: 'danger',
      in_shop: 'danger',
      inactive: 'neutral',
    }[state] || 'neutral'
  );
}

/** "Truck", "Trailer" or "Trailer · Reefer" for a unit row. */
export function unitKindLabel(unit) {
  if (!unit || unit.type !== 'trailer') return 'Truck';
  return unit.trailer_kind_label ? `Trailer · ${unit.trailer_kind_label}` : 'Trailer';
}

/** "just now", "12 min ago", "2 h ago", "3 days ago" for an age in minutes. */
export function ageLabel(minutes) {
  const n = Number(minutes);
  if (!Number.isFinite(n) || n < 1) return 'just now';
  if (n < 60) return `${Math.floor(n)} min ago`;
  if (n < 48 * 60) return `${Math.floor(n / 60)} h ago`;
  return `${Math.floor(n / (24 * 60))} days ago`;
}

/**
 * Where a unit was last seen (EquipmentAvailability.locationsFor): "Near
 * North Yard · 12 min ago", or "Last seen 2 h ago" when no saved location is
 * within a kilometre. Null when nothing has reported a position. `stale` is
 * the server's freshness, for a muted style; it never hides the line.
 */
export function locationLine(location) {
  if (!location || !location.observed_at) return null;
  const age = ageLabel(location.age_minutes);
  const text =
    location.near && location.near.name
      ? `Near ${location.near.name} · ${age}`
      : `Last seen ${age}`;
  return { text, stale: location.freshness === 'stale' };
}

// ------------------------------------------------- HOS clocks (spec 46 §1.7)
//
// Display only. These print what the carrier's ELD provider reported and
// when; nothing here judges hours, colours a clock, or says whether anyone
// may drive.

const DUTY_WORDS = Object.freeze({
  driving: 'Driving',
  on_duty: 'On duty',
  off_duty: 'Off duty',
  sleeper: 'Sleeper berth',
  yard_move: 'Yard move',
  personal_conveyance: 'Personal conveyance',
});

/** A vendor clock in seconds as "6h 10m" (floored to the minute; below zero is 0m), or null. */
export function hosDuration(seconds) {
  if (seconds === null || seconds === undefined || seconds === '') return null;
  const n = Number(seconds);
  if (!Number.isFinite(n)) return null;
  return minutesLabel(Math.max(0, Math.floor(n / 60)));
}

/** The duty-status code in words, or null. */
export function dutyStatusLabel(code) {
  return DUTY_WORDS[code] || null;
}

/** "2 min ago" for an ISO instant against `now` (ms or Date), or null. */
export function ageSince(iso, now = Date.now()) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const nowMs = now instanceof Date ? now.getTime() : Number(now);
  return ageLabel((nowMs - t) / 60000);
}

/**
 * The picker's line for one driver's clocks (a GET /hos/clocks `drivers`
 * entry): "6h 10m drive left · Samsara · 2 min ago", with "(may be out of
 * date)" when the read was stale. Null without an entry.
 */
export function hosLine(entry, now = Date.now()) {
  if (!entry) return null;
  const drive = hosDuration(entry.drive_remaining_s);
  const parts = [
    drive !== null
      ? `${drive} drive left`
      : dutyStatusLabel(entry.duty_status) || 'No drive clock reported',
  ];
  if (entry.label) parts.push(entry.label);
  const age = ageSince(entry.fetched_at, now);
  if (age) parts.push(age);
  const line = parts.join(' · ');
  return entry.stale ? `${line} (may be out of date)` : line;
}

/**
 * The second, smaller line: shift, cycle and break as the provider reported
 * them, the duty status, and how the vendor driver was matched. Missing
 * figures are left out, never guessed.
 */
export function hosDetail(entry) {
  if (!entry) return null;
  const parts = [];
  const shift = hosDuration(entry.shift_remaining_s);
  const cycle = hosDuration(entry.cycle_remaining_s);
  const brk = hosDuration(entry.break_remaining_s);
  if (shift !== null) parts.push(`Shift ${shift} left`);
  if (cycle !== null) parts.push(`Cycle ${cycle} left`);
  if (brk !== null) parts.push(`Break in ${brk}`);
  const duty = dutyStatusLabel(entry.duty_status);
  if (duty) parts.push(duty);
  if (entry.matched_by) parts.push(`matched by ${entry.matched_by}`);
  return parts.length ? parts.join(' · ') : null;
}

/** "Samsara", "Samsara and Motive", "A, B and C". */
export function listPhrase(items) {
  const list = (items || []).filter(Boolean);
  if (list.length <= 1) return list.join('');
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}

/** The picker's footer sentence: where the clocks come from, and what Ladeway does not do. */
export function hosSourceSentence(providers) {
  const labels = [...new Set((providers || []).map(p => p && p.label).filter(Boolean))];
  const from = labels.length ? listPhrase(labels) : 'your ELD provider';
  return `Hours-of-service clocks come from ${from}. Ladeway shows what the provider reports and does not check hours of service.`;
}
